import {Product} from "../../../../model/Product";

export class PossibleMatch {
  product: Product;
  /** Raw number of tokens the incoming code and the product had in common. */
  matches: number;
  /**
   * Normalized 0..1 confidence for this match. 1.0 means every token of the
   * incoming code (sku / title) was found on the product (title /
   * customerProduct_ID).
   *
   * This is what a colour-coded indicator should be driven from.
   * `matches / maxPossibleMatchIntersections` only says how this candidate
   * compares to the other candidates, so the best of a set of uniformly
   * terrible matches still came out a confident green.
   */
  score: number;

  /**
   * True when the incoming code was **byte-for-byte** equal to the product's
   * identifier in the field the EcommerceConnection nominates.
   *
   * This is deliberately as strict as the server: order ingestion does an
   * exact, case-sensitive string comparison, so this is the only thing we are
   * entitled to present as a certain match.
   */
  exact: boolean;

  /**
   * True when the values are equal only once case and separators are ignored
   * ("MATH-1A" vs "math 1a").
   *
   * Almost certainly the same product to a human, but the server will NOT
   * match it, so future orders on this connection will not resolve on their
   * own. Surfaced separately so the operator can see the data needs cleaning
   * rather than being told it is fine.
   */
  nearExact: boolean;

  /**
   * Which Product field produced the strongest hit. Shown to the operator so a
   * surprising suggestion is explicable.
   */
  matchedOn: string | null;

  /**
   * Sort key. Normally the token-overlap count, but hits in the preferred
   * field are weighted and exact/near-exact matches are pinned to the top.
   * Kept separate from `matches` so the raw overlap count stays honest.
   */
  rank: number;

  constructor (matches: number, product: Product, init?: {
    score?: number,
    exact?: boolean,
    nearExact?: boolean,
    matchedOn?: string | null,
    rank?: number
  }) {
    this.matches = matches;
    this.product = product;
    this.score = init && init.score !== undefined ? init.score : 0;
    this.exact = !!(init && init.exact);
    this.nearExact = !!(init && init.nearExact);
    this.matchedOn = init && init.matchedOn !== undefined ? init.matchedOn : null;
    this.rank = init && init.rank !== undefined ? init.rank : matches;
  }
}
